import React, { Fragment, useState } from 'react';
import { PaymentProvider } from '../../../../XV2';
import { UiGrid } from '@open-stack/ui';
import { useAsync } from '../../../../hooks/useAsync';
import { ProviderBamboraButton } from '../../bambora/button/ProviderBamboraButton';
import { apiProviderPaymentBamboraPreparePost } from '../../../../api/provider.payment.bambora.prepare.[post]';
import { useZone } from '../../../../hooks/zone';
import { Bambora } from '../../../../services/bambora';
import { toastShowError } from '@orderx/lab/xr/toast';
import { XAlert, XAlertType } from '../../../x/alert/XAlert';
import { utilLog } from '../../../../util/utilLog';

type BamboraSession = any;

interface Props {
    config: {
        providers: PaymentProvider[];
    };
    onSuccess: any;
}

export function ProviderPayBambora({ config, onSuccess }: Props) {
    const { zoneId } = useZone();
    const { providers } = config;
    const [isSuccess, setIsSuccess] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);

    const handleProvider = useAsync(async (provider) => {
        const response = await apiProviderPaymentBamboraPreparePost({
            body: { ...config, zoneId, provider },
        });

        setIsSubmitted(true);

        const { session, orderId, paymentId } = response as {
            amount: number;
            orderId?: number;
            paymentId?: number;
            provider: PaymentProvider;
            session: BamboraSession;
        };

        try {
            if (provider === PaymentProvider.BAMBORA) {
                await Bambora.openModalWithToken(session.token);
            } else if ([PaymentProvider.BAMBORA_VIPPS, PaymentProvider.BAMBORA_MOBILE_PAY].includes(provider)) {
                await Bambora.openWallet(session.sessionId);
            } else {
                return toastShowError('Unknown provider');
            }

            setIsSuccess(true);

            onSuccess({
                type: 'SUCCESS',
                orderId,
                paymentId,
            });
        } catch (error) {
            utilLog('ProviderPayBambora.handleProvider.error', error);
            toastShowError('Something went wrong. Please try again');
            // setIsSubmitted(false);
        }
    });

    const buttonByProvider = {
        [PaymentProvider.BAMBORA]: (
            <ProviderBamboraButton
                provider={PaymentProvider.BAMBORA}
                onClick={() => handleProvider.run(PaymentProvider.BAMBORA)}
                isLoading={handleProvider.isLoading}>
                Visa/Mastercard
            </ProviderBamboraButton>
        ),
        [PaymentProvider.BAMBORA_VIPPS]: (
            <ProviderBamboraButton
                provider={PaymentProvider.BAMBORA_VIPPS}
                onClick={() => handleProvider.run(PaymentProvider.BAMBORA_VIPPS)}
                isLoading={handleProvider.isLoading}>
                Vipps
            </ProviderBamboraButton>
        ),
        [PaymentProvider.BAMBORA_MOBILE_PAY]: (
            <ProviderBamboraButton
                provider={PaymentProvider.BAMBORA_MOBILE_PAY}
                onClick={() => handleProvider.run(PaymentProvider.BAMBORA_MOBILE_PAY)}
                isLoading={handleProvider.isLoading}>
                Mobile Pay
            </ProviderBamboraButton>
        ),
    };

    if (isSubmitted) {
        return <XAlert type={XAlertType.SUCCESS}>Payment started</XAlert>;
    }

    if (isSuccess) {
        return <XAlert type={XAlertType.SUCCESS}>Payment successful!</XAlert>;
    }

    return (
        <UiGrid>
            {providers.map((provider) => (
                <Fragment key={provider}>{buttonByProvider[provider]}</Fragment>
            ))}
        </UiGrid>
    );
}
